import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { localizedRoutes } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'

const categoryToProductSlug: Record<string, string> = {
  mortadelle: 'mortadella',
  prosciuttiCrudi: 'prosciutto-parma',
  prosciuttiCotti: 'prosciutto',
  salami: 'salami',
  arrosti: 'arrosti',
  specialitaStagionate: 'guanciale',
  affettati: 'affettati',
  wurstel: 'wurstel',
  cubettiTranci: 'cubetti-tranci',
  precotti: 'precotti',
}

interface ProductCategory {
  key: string
  translationKey: string
  image: string
  imageAlt: string
}

const productCategories: ProductCategory[] = [
  {
    key: 'mortadelle',
    translationKey: 'nav.productCategories.mortadelle',
    image: '/images/mortedelle.png',
    imageAlt: 'mortadelle',
  },
  {
    key: 'prosciuttiCrudi',
    translationKey: 'nav.productCategories.prosciuttiCrudi',
    image: '/images/crudi.png',
    imageAlt: 'prosciutti crudi',
  },
  {
    key: 'prosciuttiCotti',
    translationKey: 'nav.productCategories.prosciuttiCotti',
    image: '/images/cotti.png',
    imageAlt: 'prosciutti cotti',
  },
  {
    key: 'salami',
    translationKey: 'nav.productCategories.salami',
    image: '/images/salami.png',
    imageAlt: 'salami',
  },
  {
    key: 'arrosti',
    translationKey: 'nav.productCategories.arrosti',
    image: '/images/arrosti.jpg',
    imageAlt: 'arrosti',
  },
  {
    key: 'specialitaStagionate',
    translationKey: 'nav.productCategories.specialitaStagionate',
    image: '/images/stagionati.png',
    imageAlt: 'specialita stagionate',
  },
  {
    key: 'affettati',
    translationKey: 'nav.productCategories.affettati',
    image: '/images/affettati.png',
    imageAlt: 'affettati',
  },
  {
    key: 'wurstel',
    translationKey: 'nav.productCategories.wurstel',
    image: '/images/wurstel.png',
    imageAlt: 'wurstel',
  },
  {
    key: 'cubettiTranci',
    translationKey: 'nav.productCategories.cubettiTranci',
    image: '/images/cubetti-tranci.png',
    imageAlt: 'cubetti e tranci',
  },
  {
    key: 'precotti',
    translationKey: 'nav.productCategories.precotti',
    image: '/images/precotti.jpg',
    imageAlt: 'precotti',
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
}

function CategoryCard({ category, index }: { category: ProductCategory; index: number }) {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const lang = i18n.language as SupportedLanguage
  const productsPath = localizedRoutes.products[lang]
  const slug = categoryToProductSlug[category.key]

  const handleClick = () => {
    if (slug) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      navigate(`/${lang}/${productsPath}/${slug}`)
    }
  }

  return (
    <motion.div
      variants={itemVariants}
      onClick={handleClick}
      className={cn(
        'group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 cursor-pointer',
        'border border-[#F3E4D6] hover:border-[#DF631E]/30'
      )}
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-[#FDF8F3] to-[#F3E4D6]/30">
        <img
          src={category.image}
          alt={t(`products.categories.${category.key}.alt`)}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading={index < 4 ? 'eager' : 'lazy'}
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Content */}
      <div className="p-5 sm:p-6">
        <h3 className="font-serif text-lg sm:text-xl font-semibold text-[#4E2118] mb-2 group-hover:text-[#DF631E] transition-colors duration-300">
          {t(category.translationKey)}
        </h3>
        <p className="text-sm text-[#4E2118]/70 mb-4 line-clamp-2">
          {t(`products.categories.${category.key}.description`)}
        </p>

        {/* CTA Button */}
        <div
          className={cn(
            'inline-flex items-center gap-2 text-sm font-semibold',
            'text-[#DF631E] hover:text-[#B85018] transition-colors duration-300'
          )}
          aria-label={t('products.exploreCategory', { category: t(category.translationKey) })}
        >
          <span>{t('buttons.discover')}</span>
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </div>
      </div>

      {/* Corner accent */}
      <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-24 h-24 bg-[#DF631E]/10 rotate-45 translate-x-12 -translate-y-12 group-hover:bg-[#DF631E]/20 transition-colors duration-300" />
      </div>
    </motion.div>
  )
}

export function ProductCategories() {
  const { t } = useTranslation('common')

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-[#FDF8F3] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4E2118] mb-4"
          >
            {t('products.categoriesTitle')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg text-[#4E2118]/70 max-w-2xl mx-auto"
          >
            {t('products.categoriesSubtitle')}
          </motion.p>
        </div>

        {/* Categories Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8"
        >
          {productCategories.map((category, index) => (
            <CategoryCard key={category.key} category={category} index={index} />
          ))}
        </motion.div>
      </div>
    </section>
  )
}
